import React, { createContext, useState, useContext, useEffect } from 'react';

// Translation object for multi-language support across entire website
export const translations = {
    en: {
        // Navbar
        courses: 'Courses',
        browseCourses: 'Browse All Courses',
        myCourses: 'My Courses',
        recommended: 'Recommended For You',
        viewPlans: 'View Plans',
        institution: 'Institution',
        aboutUs: 'About Us',
        faculty: 'Meet Our Faculty',
        methodology: 'Our Methodology',
        contactUs: 'Contact Us',
        successStories: 'Success Stories',
        pricing: 'Pricing',
        resources: 'Resources',
        blog: 'Blog',
        faqs: 'FAQs',
        notifications: 'Notifications',
        viewAllNotifications: 'View All Notifications',
        noNotifications: 'No notifications',
        notificationPreferences: 'Notification Preferences',
        notificationType: 'Notification Type',
        inAppNotifications: 'In-App Notifications',
        emailNotifications: 'Email Notifications',
        whatsappNotifications: 'WhatsApp Notifications',
        streakReminder: 'Streak Reminders',
        achievementUnlocked: 'Achievements',
        courseUpdates: 'Course Updates',
        milestones: 'Milestones',
        leaderboardRank: 'Leaderboard',
        announcements: 'Announcements',
        login: 'Login',
        signUp: 'Sign Up',
        myProfile: 'My Profile',
        settings: 'Settings',
        help: 'Help & Support',
        signOut: 'Sign Out',
        practice: 'Practice',
        rewards: 'Rewards & Shop',
        instructorDashboard: 'Instructor Dashboard',
        adminPanel: 'Admin Panel',
        
        // Home Page
        heroTitle: 'Master English Communication with',
        heroSubtitle: 'Confidence-First Approach',
        heroDescription: 'AI-powered platform that builds communication confidence across speaking, writing, reading, and listening through personalized practice sessions',
        heroTag1: '🚀 Elevate Your Communication',
        heroTag2: '🎓 Comprehensive Curriculum',
        heroTag3: '🌍 Global Community',
        heroSubtitle1: 'Join the AI-powered revolution in language learning. Master English with real-time feedback and interactive modules.',
        heroSubtitle2: 'Structured courses designed by experts. Whether you are a beginner or advanced, we have the right path for you.',
        heroSubtitle3: 'Connect with peers, practice in real scenarios, and get certified. Your journey to confidence starts here.',
        startLearningNow: 'Start Learning Now',
        exploreCourses: 'Explore Courses',
        viewAllCourses: 'View All Courses',
        freeTrial: 'Free Trial',
        joinCommunity: 'Join Community',
        getStarted: 'Get Started Free',
        watchDemo: 'Watch Demo',
        trustedBy: 'Trusted by',
        students: 'students',
        worldwide: 'worldwide',
        aiPowered: 'AI-Powered',
        personalizedLearning: 'Personalized Learning',
        expertInstructors: 'Expert Instructors',
        interactiveLearning: 'Interactive Learning',
        interactiveLearningDesc: 'AI-driven practice sessions that adapt to your pace and learning style.',
        expertLedCourses: 'Expert Led Courses',
        expertLedCoursesDesc: 'Access 50+ Premium Modules designed by language experts',
        globalNetwork: 'Global Network',
        globalNetworkDesc: 'Connect with peers from 100+ countries instantly',
        whyChooseUs: 'Why Choose Us?',
        whyChooseUsDesc: 'We combine cutting-edge technology with proven pedagogical methods to help you update your skills efficiently.',
        aiPoweredFeedback: 'AI-Powered Feedback',
        aiPoweredFeedbackDesc: 'Instant pronunciation and grammar correction.',
        immersiveScenarios: 'Immersive Scenarios',
        immersiveScenariosDesc: 'Practice real-world conversations in a safe environment.',
        personalizedPath: 'Personalized Path',
        personalizedPathDesc: 'Adaptive curriculum that grows with your skills.',
        accreditationsPartnerships: 'Our Accreditations & Partnerships',
        explorePrograms: 'Explore Our Programs',
        exploreProgramsDesc: 'Curated learning paths for every stage of your journey',
        enrollNow: 'Enroll Now',
        learnersEmpowered: 'Learners Empowered',
        placementSupport: 'Placement Support',
        studentRating: 'Student Rating',
        enrolled: 'Enrolled',
        
        // Buttons & Actions
        startNow: 'Start Now',
        learnMore: 'Learn More',
        tryFree: 'Try Free',
        bookDemo: 'Book Demo',
        continue: 'Continue',
        cancel: 'Cancel',
        save: 'Save',
        delete: 'Delete',
        edit: 'Edit',
        submit: 'Submit',
        back: 'Back',
        next: 'Next',
        finish: 'Finish',
        close: 'Close',
        
        // Common Labels
        name: 'Name',
        email: 'Email',
        password: 'Password',
        confirmPassword: 'Confirm Password',
        phoneNumber: 'Phone Number',
        address: 'Address',
        city: 'City',
        country: 'Country',
        language: 'Language',
        
        // Authentication & Forms
        welcomeBack: 'Welcome Back',
        signInToContinue: 'Sign in to continue your learning journey',
        emailAddress: 'Email Address',
        enterYourEmail: 'your@email.com',
        enterYourPassword: 'Enter your password',
        forgotPassword: 'Forgot Password?',
        signIn: 'Sign In',
        signingIn: 'Signing in...',
        or: 'or',
        continueWithGoogle: 'Continue with Google',
        dontHaveAccount: "Don't have an account?",
        createAccount: 'Create Account',
        secureLoginProtected: 'Secure login protected by encryption',
        recordPracticeSpeaking: 'Practice Speaking & Communication',
        aiPoweredAnalysis: 'AI-Powered Analysis',
        trackYourProgress: 'Track Your Progress',
        expertCuratedLessons: 'Expert-Curated Lessons',
        masterEnglishSpeaking: 'Master Communication Skills with AI-Powered Feedback',
        showPassword: 'Show password',
        hidePassword: 'Hide password',
        fullName: 'Full Name',
        enterYourName: 'Enter your name',
        minChars: 'Min 6 chars',
        confirm: 'Confirm',
        signUp: 'Sign Up',
        creatingAccount: 'Creating Account...',
        signUpWithGoogle: 'Sign up with Google',
        alreadyHaveAccount: 'Already have an account?',
        joinThousandsOfLearners: 'Join thousands of learners today',
        realTimePronunciation: 'Real-time Pronunciation Feedback',
        detailedProgressAnalytics: 'Detailed Progress Analytics',
        gamifiedLearning: 'Gamified Learning Experience',
        masterEnglishWithConfidence: 'Master English Communication with Confidence',
        
        // Student Portal
        dashboard: 'Dashboard',
        myLearning: 'My Learning',
        progress: 'Progress',
        achievements: 'Achievements',
        certificates: 'Certificates',
        leaderboard: 'Leaderboard',
        analytics: 'Analytics',
        performanceAnalytics: 'Performance Analytics',
        explore: 'Explore',
        dictionary: 'Dictionary',
        schedule: 'Schedule',
        logOut: 'Log Out',
        search: 'Search...',
        practiceModes: 'Practice Modes',
        reading: 'Reading',
        speaking: 'Speaking',
        listening: 'Listening',
        statistics: 'Statistics',
        exploreCourses: 'Explore Courses',
        masterEnglishCurated: 'Build communication skills with our curated courses.',
        loadingCourses: 'Loading courses...',
        searchNewWords: 'Search for new words and improve your vocabulary here.',
        viewSchedule: 'View your live class schedule and book practice slots.',
        yourName: 'Your name',
        nameCannotBeEmpty: 'Name cannot be empty.',
        enterValidEmail: 'Please enter a valid email address.',
        saving: 'Saving...',
        saveChanges: 'Save Changes',
        profileUpdatedSuccess: 'Profile updated successfully.',
        failedToUpdateProfile: 'Failed to update profile.',
        unexpectedResponse: 'Unexpected response from server.',
        loadingDashboard: 'Loading Dashboard...',
        quickStart: 'Quick Start',
        speakingExam: 'Communication Exam',
        practicePronunciation: 'Practice pronunciation',
        writing: 'Writing',
        minTask: '15 min task',
        recordingFeatureRemoved: 'Recording feature removed.',
        recordingFeatureDisabled: 'The speech recording and analysis feature has been disabled per project configuration.',
        terms: 'Terms',
        enrollNow: 'Enroll Now',
        
        // Course Related
        courseTitle: 'Course Title',
        courseDescription: 'Course Description',
        startCourse: 'Start Course',
        continueCourse: 'Continue Course',
        completedCourses: 'Completed Courses',
        inProgress: 'In Progress',
        notStarted: 'Not Started',
        lessons: 'Lessons',
        duration: 'Duration',
        level: 'Level',
        beginner: 'Beginner',
        intermediate: 'Intermediate',
        advanced: 'Advanced',
        
        // Practice & Sessions
        startPractice: 'Start Practice',
        recordAudio: 'Record Audio',
        playback: 'Playback',
        submit: 'Submit',
        retry: 'Retry',
        feedback: 'Feedback',
        grammarScore: 'Grammar Score',
        fluencyScore: 'Fluency Score',
        pronunciationScore: 'Pronunciation Score',
        vocabularyScore: 'Vocabulary Score',
        
        // Rewards & Gamification
        zenCoins: 'Zen Coins',
        xpPoints: 'XP Points',
        currentStreak: 'Current Streak',
        longestStreak: 'Longest Streak',
        dailyGoal: 'Daily Goal',
        redeemRewards: 'Redeem Rewards',
        unlockAchievement: 'Unlock Achievement',
        rewardsHub: 'Rewards Hub',
        currentBalance: 'Current Balance',
        redeemYourCoins: 'Redeem your hard-earned ZenCoins for exclusive perks.',
        topLearners: 'Top Learners',
        inviteFriends: 'Invite Friends, Earn Coins',
        inviteFriendsDesc: 'Get 500 ZenCoins for every friend who joins properly.',
        copyLink: 'Copy Link',
        redeem: 'Redeem',
        premiumDarkTheme: 'Premium Dark Theme',
        masterBadge: 'Master Badge',
        oneOnOneMentor: '1-on-1 Mentor Call',
        adFreeExperience: 'Ad-Free Experience',
        
        // Practice Page
        practiceArena: 'Practice Arena',
        practiceArenaDesc: 'Master your skills with daily challenges. Earn ZenCoins for every correct answer!',
        vocabKataComingSoon: 'VocabKata Coming Soon',
        vocabKataDesc: 'Expand your word bank with AI-powered flashcards.',
        yourPerformance: 'Your Performance',
        dailyStreak: 'Daily Streak',
        problemsSolved: 'Problems Solved',
        proTip: 'Pro Tip',
        proTipDesc: 'Consistency is key! Solving just 1 Kata a day improves retention by 40%.',
        days: 'Days',
        
        // Courses Page
        exploreOurPrograms: 'Explore Our Programs',
        curriculumByExperts: 'Curriculum designed by our experts to transform your communication skills',
        loadingCoursesMsg: 'Loading courses...',
        noCoursesAvailable: 'No courses available yet',
        noCoursesInCategory: 'No courses in',
        instructorsCreating: 'Our instructors are creating amazing courses for you. Check back soon!',
        tryDifferentCategory: 'Try selecting a different category or check back later.',
        viewAllCoursesBtn: 'View All Courses',
        allLevels: 'All Levels',
        selfPaced: 'Self-paced',
        modules: 'modules',
        
        // Footer
        about: 'About',
        aboutUs: 'About Us',
        ourMission: 'Our Mission',
        features: 'Features',
        testimonials: 'Testimonials',
        learning: 'Learning',
        practiceSessions: 'Practice Sessions',
        getFeedback: 'Get Feedback',
        trackProgress: 'Track Progress',
        resources: 'Resources',
        tutorials: 'Tutorials',
        support: 'Support',
        connect: 'Connect',
        community: 'Community',
        events: 'Events',
        volunteer: 'Volunteer',
        followUs: 'Follow Us',
        allRightsReserved: 'All rights reserved',
        privacyPolicy: 'Privacy Policy',
        termsOfUse: 'Terms of Use',
        
        // Messages
        welcomeBack: 'Welcome Back',
        loadingMessage: 'Loading...',
        noDataAvailable: 'No data available',
        successMessage: 'Success!',
        errorMessage: 'Oops! Something went wrong',
        confirmDelete: 'Are you sure you want to delete?',
        saveSuccess: 'Saved successfully',
        updateSuccess: 'Updated successfully',
        
        // Footer
        aboutCompany: 'About Company',
        privacyPolicy: 'Privacy Policy',
        termsOfService: 'Terms of Service',
        contactSupport: 'Contact Support',
        followUs: 'Follow Us',
        allRightsReserved: 'All rights reserved',
    },
    es: {
        // Navbar
        courses: 'Cursos',
        browseCourses: 'Ver Todos los Cursos',
        myCourses: 'Mis Cursos',
        recommended: 'Recomendado Para Ti',
        viewPlans: 'Ver Planes',
        institution: 'Institución',
        aboutUs: 'Sobre Nosotros',
        faculty: 'Nuestro Profesorado',
        methodology: 'Nuestra Metodología',
        contactUs: 'Contáctanos',
        successStories: 'Historias de Éxito',
        pricing: 'Precios',
        resources: 'Recursos',
        blog: 'Blog',
        faqs: 'Preguntas Frecuentes',
        notifications: 'Notificaciones',
        viewAllNotifications: 'Ver Todas las Notificaciones',
        noNotifications: 'Sin notificaciones',
        notificationPreferences: 'Preferencias de Notificación',
        notificationType: 'Tipo de Notificación',
        inAppNotifications: 'Notificaciones en Aplicación',
        emailNotifications: 'Notificaciones por Correo',
        whatsappNotifications: 'Notificaciones de WhatsApp',
        streakReminder: 'Recordatorios de Racha',
        achievementUnlocked: 'Logros',
        courseUpdates: 'Actualizaciones de Cursos',
        milestones: 'Hitos',
        leaderboardRank: 'Tabla de Clasificación',
        announcements: 'Anuncios',
        login: 'Iniciar Sesión',
        signUp: 'Registrarse',
        myProfile: 'Mi Perfil',
        settings: 'Configuración',
        help: 'Ayuda y Soporte',
        signOut: 'Cerrar Sesión',
        practice: 'Práctica',
        rewards: 'Recompensas y Tienda',
        instructorDashboard: 'Panel de Instructor',
        adminPanel: 'Panel de Administrador',
        
        // Home Page
        heroTitle: 'Domina la Comunicación en Inglés con',
        heroSubtitle: 'Enfoque en la Confianza Primero',
        heroDescription: 'Plataforma impulsada por IA que fortalece tu comunicación en habla, escritura, lectura y escucha con práctica personalizada',
        heroTag1: '🚀 Eleva Tu Comunicación',
        heroTag2: '🎓 Currículo Integral',
        heroTag3: '🌍 Comunidad Global',
        heroSubtitle1: 'Únete a la revolución impulsada por IA en el aprendizaje de idiomas. Domina el inglés con retroalimentación en tiempo real y módulos interactivos.',
        heroSubtitle2: 'Cursos estructurados diseñados por expertos. Ya seas principiante o avanzado, tenemos el camino correcto para ti.',
        heroSubtitle3: 'Conéctate con compañeros, practica en escenarios reales y obtén certificación. Tu viaje hacia la confianza comienza aquí.',
        startLearningNow: 'Comenzar a Aprender Ahora',
        exploreCourses: 'Explorar Cursos',
        viewAllCourses: 'Ver Todos los Cursos',
        freeTrial: 'Prueba Gratis',
        joinCommunity: 'Unirse a la Comunidad',
        getStarted: 'Comenzar Gratis',
        watchDemo: 'Ver Demo',
        trustedBy: 'Confiado por',
        students: 'estudiantes',
        worldwide: 'en todo el mundo',
        aiPowered: 'Impulsado por IA',
        personalizedLearning: 'Aprendizaje Personalizado',
        expertInstructors: 'Instructores Expertos',
        interactiveLearning: 'Aprendizaje Interactivo',
        interactiveLearningDesc: 'Sesiones de práctica impulsadas por IA que se adaptan a tu ritmo y estilo de aprendizaje.',
        expertLedCourses: 'Cursos Dirigidos por Expertos',
        expertLedCoursesDesc: 'Accede a más de 50 módulos premium diseñados por expertos en idiomas',
        globalNetwork: 'Red Global',
        globalNetworkDesc: 'Conéctate con compañeros de más de 100 países al instante',
        whyChooseUs: '¿Por Qué Elegirnos?',
        whyChooseUsDesc: 'Combinamos tecnología de vanguardia con métodos pedagógicos probados para ayudarte a actualizar tus habilidades de manera eficiente.',
        aiPoweredFeedback: 'Retroalimentación Impulsada por IA',
        aiPoweredFeedbackDesc: 'Corrección instantánea de pronunciación y gramática.',
        immersiveScenarios: 'Escenarios Inmersivos',
        immersiveScenariosDesc: 'Practica conversaciones del mundo real en un entorno seguro.',
        personalizedPath: 'Ruta Personalizada',
        personalizedPathDesc: 'Currículo adaptativo que crece con tus habilidades.',
        accreditationsPartnerships: 'Nuestras Acreditaciones y Asociaciones',
        explorePrograms: 'Explora Nuestros Programas',
        exploreProgramsDesc: 'Rutas de aprendizaje seleccionadas para cada etapa de tu viaje',
        enrollNow: 'Inscribirse Ahora',
        learnersEmpowered: 'Estudiantes Empoderados',
        placementSupport: 'Apoyo de Colocación',
        studentRating: 'Calificación de Estudiantes',
        enrolled: 'Inscrito',
        
        // Buttons & Actions
        startNow: 'Comenzar Ahora',
        learnMore: 'Saber Más',
        tryFree: 'Prueba Gratis',
        bookDemo: 'Reservar Demo',
        continue: 'Continuar',
        cancel: 'Cancelar',
        save: 'Guardar',
        delete: 'Eliminar',
        edit: 'Editar',
        submit: 'Enviar',
        back: 'Atrás',
        next: 'Siguiente',
        finish: 'Finalizar',
        close: 'Cerrar',
        
        // Common Labels
        name: 'Nombre',
        email: 'Correo Electrónico',
        password: 'Contraseña',
        confirmPassword: 'Confirmar Contraseña',
        phoneNumber: 'Número de Teléfono',
        address: 'Dirección',
        city: 'Ciudad',
        country: 'País',
        language: 'Idioma',
        
        // Authentication & Forms
        welcomeBack: 'Bienvenido de Nuevo',
        signInToContinue: 'Inicia sesión para continuar tu viaje de aprendizaje',
        emailAddress: 'Dirección de Correo Electrónico',
        enterYourEmail: 'tu@correo.com',
        enterYourPassword: 'Ingresa tu contraseña',
        forgotPassword: '¿Olvidaste la Contraseña?',
        signIn: 'Iniciar Sesión',
        signingIn: 'Iniciando sesión...',
        or: 'o',
        continueWithGoogle: 'Continuar con Google',
        dontHaveAccount: '¿No tienes una cuenta?',
        createAccount: 'Crear Cuenta',
        secureLoginProtected: 'Inicio de sesión seguro protegido por encriptación',
        recordPracticeSpeaking: 'Practicar Habla y Comunicación',
        aiPoweredAnalysis: 'Análisis Impulsado por IA',
        trackYourProgress: 'Seguir Tu Progreso',
        expertCuratedLessons: 'Lecciones Curadas por Expertos',
        masterEnglishSpeaking: 'Domina la Comunicación en Inglés con Retroalimentación Impulsada por IA',
        showPassword: 'Mostrar contraseña',
        hidePassword: 'Ocultar contraseña',
        fullName: 'Nombre Completo',
        enterYourName: 'Ingresa tu nombre',
        minChars: 'Mín 6 caracteres',
        confirm: 'Confirmar',
        signUp: 'Registrarse',
        creatingAccount: 'Creando Cuenta...',
        signUpWithGoogle: 'Registrarse con Google',
        alreadyHaveAccount: '¿Ya tienes una cuenta?',
        joinThousandsOfLearners: 'Únete a miles de estudiantes hoy',
        realTimePronunciation: 'Retroalimentación de Pronunciación en Tiempo Real',
        detailedProgressAnalytics: 'Análisis de Progreso Detallado',
        gamifiedLearning: 'Experiencia de Aprendizaje Gamificada',
        masterEnglishWithConfidence: 'Domina la Comunicación en Inglés con Confianza',
        
        // Student Portal
        dashboard: 'Panel de Control',
        myLearning: 'Mi Aprendizaje',
        progress: 'Progreso',
        achievements: 'Logros',
        certificates: 'Certificados',
        leaderboard: 'Tabla de Clasificación',
        analytics: 'Análisis',
        performanceAnalytics: 'Análisis de Rendimiento',
        explore: 'Explorar',
        dictionary: 'Diccionario',
        schedule: 'Horario',
        logOut: 'Cerrar Sesión',
        search: 'Buscar...',
        practiceModes: 'Modos de Práctica',
        reading: 'Lectura',
        speaking: 'Habla',
        listening: 'Escucha',
        statistics: 'Estadísticas',
        exploreCourses: 'Explorar Cursos',
        masterEnglishCurated: 'Mejora tus habilidades de comunicación con nuestros cursos curados.',
        loadingCourses: 'Cargando cursos...',
        searchNewWords: 'Busca nuevas palabras y mejora tu vocabulario aquí.',
        viewSchedule: 'Ver tu horario de clases en vivo y reservar espacios de práctica.',
        yourName: 'Tu nombre',
        nameCannotBeEmpty: 'El nombre no puede estar vacío.',
        enterValidEmail: 'Por favor ingresa una dirección de correo válida.',
        saving: 'Guardando...',
        saveChanges: 'Guardar Cambios',
        profileUpdatedSuccess: 'Perfil actualizado exitosamente.',
        failedToUpdateProfile: 'Error al actualizar el perfil.',
        unexpectedResponse: 'Respuesta inesperada del servidor.',
        loadingDashboard: 'Cargando Panel...',
        quickStart: 'Inicio Rápido',
        speakingExam: 'Examen de Comunicación',
        practicePronunciation: 'Practicar pronunciación',
        writing: 'Escritura',
        minTask: 'Tarea de 15 min',
        recordingFeatureRemoved: 'Función de grabación eliminada.',
        recordingFeatureDisabled: 'La función de grabación y análisis de voz ha sido deshabilitada según la configuración del proyecto.',
        terms: 'Términos',
        enrollNow: 'Inscribirse Ahora',
        
        // Course Related
        courseTitle: 'Título del Curso',
        courseDescription: 'Descripción del Curso',
        startCourse: 'Comenzar Curso',
        continueCourse: 'Continuar Curso',
        completedCourses: 'Cursos Completados',
        inProgress: 'En Progreso',
        notStarted: 'No Iniciado',
        lessons: 'Lecciones',
        duration: 'Duración',
        level: 'Nivel',
        beginner: 'Principiante',
        intermediate: 'Intermedio',
        advanced: 'Avanzado',
        
        // Practice & Sessions
        startPractice: 'Comenzar Práctica',
        recordAudio: 'Grabar Audio',
        playback: 'Reproducir',
        submit: 'Enviar',
        retry: 'Reintentar',
        feedback: 'Retroalimentación',
        grammarScore: 'Puntuación de Gramática',
        fluencyScore: 'Puntuación de Fluidez',
        pronunciationScore: 'Puntuación de Pronunciación',
        vocabularyScore: 'Puntuación de Vocabulario',
        
        // Rewards & Gamification
        zenCoins: 'Monedas Zen',
        xpPoints: 'Puntos XP',
        currentStreak: 'Racha Actual',
        longestStreak: 'Racha Más Larga',
        dailyGoal: 'Meta Diaria',
        redeemRewards: 'Canjear Recompensas',
        unlockAchievement: 'Desbloquear Logro',
        rewardsHub: 'Centro de Recompensas',
        currentBalance: 'Saldo Actual',
        redeemYourCoins: 'Canjea tus ZenCoins ganadas con esfuerzo por beneficios exclusivos.',
        topLearners: 'Mejores Estudiantes',
        inviteFriends: 'Invita Amigos, Gana Monedas',
        inviteFriendsDesc: 'Obtén 500 ZenCoins por cada amigo que se una correctamente.',
        copyLink: 'Copiar Enlace',
        redeem: 'Canjear',
        premiumDarkTheme: 'Tema Oscuro Premium',
        masterBadge: 'Insignia de Maestro',
        oneOnOneMentor: 'Llamada de Mentor 1 a 1',
        adFreeExperience: 'Experiencia Sin Anuncios',
        
        // Practice Page
        practiceArena: 'Arena de Práctica',
        practiceArenaDesc: '¡Domina tus habilidades con desafíos diarios. Gana ZenCoins por cada respuesta correcta!',
        vocabKataComingSoon: 'VocabKata Próximamente',
        vocabKataDesc: 'Expande tu banco de palabras con tarjetas de memoria impulsadas por IA.',
        yourPerformance: 'Tu Rendimiento',
        dailyStreak: 'Racha Diaria',
        problemsSolved: 'Problemas Resueltos',
        proTip: 'Consejo Profesional',
        proTipDesc: '¡La consistencia es la clave! Resolver solo 1 Kata al día mejora la retención en un 40%.',
        days: 'Días',
        
        // Courses Page
        exploreOurPrograms: 'Explora Nuestros Programas',
        curriculumByExperts: 'Currículo diseñado por nuestros expertos para transformar tus habilidades de comunicación',
        loadingCoursesMsg: 'Cargando cursos...',
        noCoursesAvailable: 'Aún no hay cursos disponibles',
        noCoursesInCategory: 'No hay cursos en',
        instructorsCreating: '¡Nuestros instructores están creando cursos increíbles para ti. Vuelve pronto!',
        tryDifferentCategory: 'Intenta seleccionar una categoría diferente o vuelve más tarde.',
        viewAllCoursesBtn: 'Ver Todos los Cursos',
        allLevels: 'Todos los Niveles',
        selfPaced: 'A tu propio ritmo',
        modules: 'módulos',
        
        // Footer
        about: 'Acerca de',
        aboutUs: 'Sobre Nosotros',
        ourMission: 'Nuestra Misión',
        features: 'Características',
        testimonials: 'Testimonios',
        learning: 'Aprendizaje',
        practiceSessions: 'Sesiones de Práctica',
        getFeedback: 'Obtener Retroalimentación',
        trackProgress: 'Seguir Progreso',
        resources: 'Recursos',
        tutorials: 'Tutoriales',
        support: 'Soporte',
        connect: 'Conectar',
        community: 'Comunidad',
        events: 'Eventos',
        volunteer: 'Voluntariado',
        followUs: 'Síguenos',
        allRightsReserved: 'Todos los derechos reservados',
        privacyPolicy: 'Política de Privacidad',
        termsOfUse: 'Términos de Uso',
        
        // Messages
        welcomeBack: 'Bienvenido de Nuevo',
        loadingMessage: 'Cargando...',
        noDataAvailable: 'No hay datos disponibles',
        successMessage: '¡Éxito!',
        errorMessage: '¡Ups! Algo salió mal',
        confirmDelete: '¿Estás seguro de que quieres eliminar?',
        saveSuccess: 'Guardado exitosamente',
        updateSuccess: 'Actualizado exitosamente',
        
        // Footer
        aboutCompany: 'Sobre la Empresa',
        privacyPolicy: 'Política de Privacidad',
        termsOfService: 'Términos de Servicio',
        contactSupport: 'Contactar Soporte',
        followUs: 'Síguenos',
        allRightsReserved: 'Todos los derechos reservados',
    },
    ta: {
        // Navbar
        courses: 'பாடங்கள்',
        browseCourses: 'அனைத்து பாடங்களையும் பார்க்கவும்',
        myCourses: 'எனது பாடங்கள்',
        recommended: 'உங்களுக்கு பரிந்துரைக்கப்பட்டது',
        viewPlans: 'திட்டங்களைக் காண்க',
        institution: 'நிறுவனம்',
        aboutUs: 'எங்களை பற்றி',
        faculty: 'எங்கள் ஆசிரியர்கள்',
        methodology: 'எங்கள் முறை',
        contactUs: 'எங்களை தொடர்பு கொள்ளுங்கள்',
        successStories: 'வெற்றிக் கதைகள்',
        pricing: 'விலை நிர்ணயம்',
        resources: 'வளங்கள்',
        blog: 'வலைப்பதிவு',
        faqs: 'அடிக்கடி கேட்கப்படும் கேள்விகள்',
        notifications: 'அறிவிப்புகள்',
        viewAllNotifications: 'அனைத்து அறிவிப்புகளையும் பார்க்கவும்',
        noNotifications: 'அறிவிப்புகள் இல்லை',
        notificationPreferences: 'அறிவிப்பு அறிவுரைகள்',
        notificationType: 'அறிவிப்பு வகை',
        inAppNotifications: 'ஆப்ப்ளிகேஷன் அறிவிப்புகள்',
        emailNotifications: 'மின்னஞ்சல் அறிவிப்புகள்',
        whatsappNotifications: 'WhatsApp அறிவிப்புகள்',
        streakReminder: 'சீரான அறிவுரைகள்',
        achievementUnlocked: 'சாதனைகள்',
        courseUpdates: 'கோர்ஸ் புதுப்பிப்புகள்',
        milestones: 'மைல்கற்கள்',
        leaderboardRank: 'தரவரிசை',
        announcements: 'அறிவிப்புகள்',
        login: 'உள்நுழைய',
        signUp: 'பதிவு செய்க',
        myProfile: 'எனது சுயவிவரம்',
        settings: 'அமைப்புகள்',
        help: 'உதவி மற்றும் ஆதரவு',
        signOut: 'வெளியேறு',
        practice: 'பயிற்சி',
        rewards: 'வெகுமதிகள் மற்றும் கடை',
        instructorDashboard: 'பயிற்றுவிப்பாளர் பலகை',
        adminPanel: 'நிர்வாகி பலகை',
        
        // Home Page
        heroTitle: 'ஆங்கில தொடர்பாடலில் நிபுணத்துவம் பெறுங்கள்',
        heroSubtitle: 'நம்பிக்கை-முதல் அணுகுமுறை',
        heroDescription: 'பேசுதல், எழுதுதல், வாசித்தல், கேட்டல் ஆகியவற்றில் உங்கள் தொடர்பாடல் நம்பிக்கையை வளர்க்கும் AI-இயங்கும் தளம்',
        heroTag1: '🚀 உங்கள் தொடர்பாடலை உயர்த்துங்கள்',
        heroTag2: '🎓 விரிவான பாடத்திட்டம்',
        heroTag3: '🌍 உலகளாவிய சமூகம்',
        heroSubtitle1: 'மொழி கற்றலில் AI-இயங்கும் புரட்சியில் சேரவும். நேரடி கருத்து மற்றும் ஊடாடும் தொகுதிகளுடன் ஆங்கிலத்தில் தேர்ச்சி பெறுங்கள்.',
        heroSubtitle2: 'நிபுணர்களால் வடிவமைக்கப்பட்ட கட்டமைக்கப்பட்ட பாடங்கள். நீங்கள் தொடக்கநிலை அல்லது மேம்பட்டவர் என்றாலும், உங்களுக்கு சரியான பாதை இங்கே உள்ளது.',
        heroSubtitle3: 'சக மாணவர்களுடன் இணையுங்கள், உண்மையான சூழ்நிலைகளில் பயிற்சி செய்யுங்கள், சான்றிதழ் பெறுங்கள். நம்பிக்கைக்கான உங்கள் பயணம் இங்கே தொடங்குகிறது.',
        startLearningNow: 'இப்போது கற்க தொடங்குங்கள்',
        exploreCourses: 'பாடங்களை ஆராயுங்கள்',
        viewAllCourses: 'அனைத்து பாடங்களையும் பார்க்கவும்',
        freeTrial: 'இலவச சோதனை',
        joinCommunity: 'சமூகத்தில் சேரவும்',
        getStarted: 'இலவசமாக தொடங்கவும்',
        watchDemo: 'டெமோவைப் பார்க்கவும்',
        trustedBy: 'நம்பப்படுகிறது',
        students: 'மாணவர்கள்',
        worldwide: 'உலகம் முழுவதும்',
        aiPowered: 'AI-இயங்கும்',
        personalizedLearning: 'தனிப்பயனாக்கப்பட்ட கற்றல்',
        expertInstructors: 'நிபுணர் பயிற்றுவிப்பாளர்கள்',
        interactiveLearning: 'ஊடாடும் கற்றல்',
        interactiveLearningDesc: 'உங்கள் வேகம் மற்றும் கற்றல் பாணிக்கு ஏற்ப AI-உந்துதல் பயிற்சி அமர்வுகள்.',
        expertLedCourses: 'நிபுணர் வழிகாட்டும் பாடங்கள்',
        expertLedCoursesDesc: 'மொழி நிபுணர்களால் வடிவமைக்கப்பட்ட 50+ பிரீமியம் தொகுதிகளை அணுகவும்',
        globalNetwork: 'உலகளாவிய வலையமைப்பு',
        globalNetworkDesc: '100+ நாடுகளில் இருந்து சக மாணவர்களுடன் உடனடியாக இணையுங்கள்',
        whyChooseUs: 'எங்களை ஏன் தேர்வு செய்ய வேண்டும்?',
        whyChooseUsDesc: 'உங்கள் திறன்களை திறம்பட புதுப்பிக்க உதவ நாங்கள் அதிநவீன தொழில்நுட்பத்தை நிரூபிக்கப்பட்ட கற்பித்தல் முறைகளுடன் இணைக்கிறோம்.',
        aiPoweredFeedback: 'AI-இயங்கும் கருத்து',
        aiPoweredFeedbackDesc: 'உடனடி உச்சரிப்பு மற்றும் இலக்கண திருத்தம்.',
        immersiveScenarios: 'அமிழ்த்தும் சூழ்நிலைகள்',
        immersiveScenariosDesc: 'பாதுகாப்பான சூழலில் உண்மையான உலக உரையாடல்களை பயிற்சி செய்யுங்கள்.',
        personalizedPath: 'தனிப்பயனாக்கப்பட்ட பாதை',
        personalizedPathDesc: 'உங்கள் திறன்களுடன் வளரும் தகவமைப்பு பாடத்திட்டம்.',
        accreditationsPartnerships: 'எங்கள் அங்கீகாரங்கள் மற்றும் கூட்டாண்மைகள்',
        explorePrograms: 'எங்கள் திட்டங்களை ஆராயுங்கள்',
        exploreProgramsDesc: 'உங்கள் பயணத்தின் ஒவ்வொரு கட்டத்திற்கும் தேர்ந்தெடுக்கப்பட்ட கற்றல் பாதைகள்',
        enrollNow: 'இப்போது சேர்க்கை',
        learnersEmpowered: 'மாணவர்கள் அதிகாரம் பெற்றனர்',
        placementSupport: 'வேலைவாய்ப்பு ஆதரவு',
        studentRating: 'மாணவர் மதிப்பீடு',
        enrolled: 'சேர்க்கப்பட்டது',
        
        // Buttons & Actions
        startNow: 'இப்போது தொடங்கவும்',
        learnMore: 'மேலும் அறிக',
        tryFree: 'இலவசமாக முயற்சிக்கவும்',
        bookDemo: 'டெமோவை பதிவு செய்க',
        continue: 'தொடரவும்',
        cancel: 'ரத்து செய்',
        save: 'சேமி',
        delete: 'நீக்கு',
        edit: 'திருத்து',
        submit: 'சமர்ப்பிக்கவும்',
        back: 'பின்னால்',
        next: 'அடுத்தது',
        finish: 'முடிக்கவும்',
        close: 'மூடு',
        
        // Common Labels
        name: 'பெயர்',
        email: 'மின்னஞ்சல்',
        password: 'கடவுச்சொல்',
        confirmPassword: 'கடவுச்சொல்லை உறுதிப்படுத்துங்கள்',
        phoneNumber: 'தொலைபேசி எண்',
        address: 'முகவரி',
        city: 'நகரம்',
        country: 'நாடு',
        language: 'மொழி',
        
        // Authentication & Forms
        welcomeBack: 'மீண்டும் வரவேற்கிறோம்',
        signInToContinue: 'உங்கள் கற்றல் பயணத்தைத் தொடர உள்நுழையவும்',
        emailAddress: 'மின்னஞ்சல் முகவரி',
        enterYourEmail: 'உங்கள்@மின்னஞ்சல்.com',
        enterYourPassword: 'உங்கள் கடவுச்சொல்லை உள்ளிடவும்',
        forgotPassword: 'கடவுச்சொல்லை மறந்துவிட்டீர்களா?',
        signIn: 'உள்நுழைய',
        signingIn: 'உள்நுழைகிறது...',
        or: 'அல்லது',
        continueWithGoogle: 'Google உடன் தொடரவும்',
        dontHaveAccount: 'கணக்கு இல்லையா?',
        createAccount: 'கணக்கை உருவாக்கவும்',
        secureLoginProtected: 'குறியாக்கத்தால் பாதுகாக்கப்பட்ட பாதுகாப்பான உள்நுழைவு',
        recordPracticeSpeaking: 'பேச்சு மற்றும் தொடர்பாடலை பயிற்சி செய்யுங்கள்',
        aiPoweredAnalysis: 'AI-இயங்கும் பகுப்பாய்வு',
        trackYourProgress: 'உங்கள் முன்னேற்றத்தை கண்காணிக்கவும்',
        expertCuratedLessons: 'நிபுணர் தேர்ந்தெடுக்கப்பட்ட பாடங்கள்',
        masterEnglishSpeaking: 'AI-இயங்கும் கருத்துடன் ஆங்கில தொடர்பாடலில் தேர்ச்சி பெறுங்கள்',
        showPassword: 'கடவுச்சொல்லைக் காட்டு',
        hidePassword: 'கடவுச்சொல்லை மறை',
        fullName: 'முழு பெயர்',
        enterYourName: 'உங்கள் பெயரை உள்ளிடவும்',
        minChars: 'குறைந்தது 6 எழுத்துக்கள்',
        confirm: 'உறுதிப்படுத்து',
        signUp: 'பதிவு செய்க',
        creatingAccount: 'கணக்கை உருவாக்குகிறது...',
        signUpWithGoogle: 'Google உடன் பதிவு செய்க',
        alreadyHaveAccount: 'ஏற்கனவே கணக்கு உள்ளதா?',
        joinThousandsOfLearners: 'இன்று ஆயிரக்கணக்கான மாணவர்களுடன் சேரவும்',
        realTimePronunciation: 'நேரலை உச்சரிப்பு கருத்து',
        detailedProgressAnalytics: 'விரிவான முன்னேற்ற பகுப்பாய்வு',
        gamifiedLearning: 'விளையாட்டு போன்ற கற்றல் அனுபவம்',
        masterEnglishWithConfidence: 'நம்பிக்கையுடன் ஆங்கில தொடர்பாடலில் தேர்ச்சி பெறுங்கள்',
        
        // Student Portal
        dashboard: 'டாஷ்போர்டு',
        myLearning: 'எனது கற்றல்',
        progress: 'முன்னேற்றம்',
        achievements: 'சாதனைகள்',
        certificates: 'சான்றிதழ்கள்',
        leaderboard: 'முன்னணி பலகை',
        analytics: 'பகுப்பாய்வு',
        performanceAnalytics: 'செயல்திறன் பகுப்பாய்வு',
        explore: 'ஆராயுங்கள்',
        dictionary: 'அகராதி',
        schedule: 'அட்டவணை',
        logOut: 'வெளியேறு',
        search: 'தேடு...',
        practiceModes: 'பயிற்சி முறைகள்',
        reading: 'வாசிப்பு',
        speaking: 'பேசுதல்',
        listening: 'கேட்டல்',
        statistics: 'புள்ளிவிவரங்கள்',
        exploreCourses: 'பாடங்களை ஆராயுங்கள்',
        masterEnglishCurated: 'எங்கள் தேர்ந்தெடுக்கப்பட்ட பாடங்களுடன் தொடர்பாடல் திறன்களை மேம்படுத்துங்கள்.',
        loadingCourses: 'பாடங்களை ஏற்றுகிறது...',
        searchNewWords: 'புதிய சொற்களைத் தேடி இங்கே உங்கள் சொல்வளத்தை மேம்படுத்துங்கள்.',
        viewSchedule: 'உங்கள் நேரடி வகுப்பு அட்டவணையைப் பார்க்கவும் மற்றும் பயிற்சி இடங்களை பதிவு செய்யவும்.',
        yourName: 'உங்கள் பெயர்',
        nameCannotBeEmpty: 'பெயர் காலியாக இருக்க முடியாது.',
        enterValidEmail: 'தயவுசெய்து சரியான மின்னஞ்சல் முகவரியை உள்ளிடவும்.',
        saving: 'சேமிக்கிறது...',
        saveChanges: 'மாற்றங்களைச் சேமிக்கவும்',
        profileUpdatedSuccess: 'சுயவிவரம் வெற்றிகரமாக புதுப்பிக்கப்பட்டது.',
        failedToUpdateProfile: 'சுயவிவரத்தை புதுப்பிக்க முடியவில்லை.',
        unexpectedResponse: 'சர்வரிலிருந்து எதிர்பாராத பதில்.',
        loadingDashboard: 'டாஷ்போர்டை ஏற்றுகிறது...',
        quickStart: 'விரைவு தொடக்கம்',
        speakingExam: 'தொடர்பாடல் தேர்வு',
        practicePronunciation: 'உச்சரிப்பு பயிற்சி',
        writing: 'எழுதுதல்',
        minTask: '15 நிமிட பணி',
        recordingFeatureRemoved: 'பதிவு அம்சம் அகற்றப்பட்டது.',
        recordingFeatureDisabled: 'திட்ட உள்ளமைவின்படி பேச்சு பதிவு மற்றும் பகுப்பாய்வு அம்சம் முடக்கப்பட்டுள்ளது.',
        terms: 'விதிமுறைகள்',
        enrollNow: 'இப்போது சேர்க்கை',
        
        // Course Related
        courseTitle: 'பாட தலைப்பு',
        courseDescription: 'பாட விளக்கம்',
        startCourse: 'பாடத்தை தொடங்கவும்',
        continueCourse: 'பாடத்தைத் தொடரவும்',
        completedCourses: 'முடிக்கப்பட்ட பாடங்கள்',
        inProgress: 'செயலில் உள்ளது',
        notStarted: 'தொடங்கப்படவில்லை',
        lessons: 'பாடங்கள்',
        duration: 'கால அளவு',
        level: 'நிலை',
        beginner: 'ஆரம்பநிலை',
        intermediate: 'இடைநிலை',
        advanced: 'மேம்பட்ட',
        
        // Practice & Sessions
        startPractice: 'பயிற்சியைத் தொடங்கவும்',
        recordAudio: 'ஆடியோவைப் பதிவு செய்யவும்',
        playback: 'மீண்டும் இயக்கவும்',
        submit: 'சமர்ப்பிக்கவும்',
        retry: 'மீண்டும் முயற்சிக்கவும்',
        feedback: 'கருத்து',
        grammarScore: 'இலக்கண மதிப்பெண்',
        fluencyScore: 'சரளத்துவ மதிப்பெண்',
        pronunciationScore: 'உச்சரிப்பு மதிப்பெண்',
        vocabularyScore: 'சொல்லாட்சி மதிப்பெண்',
        
        // Rewards & Gamification
        zenCoins: 'ஜென் நாணயங்கள்',
        xpPoints: 'XP புள்ளிகள்',
        currentStreak: 'தற்போதைய வரிசை',
        longestStreak: 'நீண்ட வரிசை',
        dailyGoal: 'தினசரி இலக்கு',
        redeemRewards: 'வெகுமதிகளை மீட்கவும்',
        unlockAchievement: 'சாதனையைத் திறக்கவும்',
        rewardsHub: 'வெகுமதி மையம்',
        currentBalance: 'தற்போதைய இருப்பு',
        redeemYourCoins: 'உங்கள் கடினமாக சம்பாதித்த ஜென் நாணயங்களை பிரத்யேக சலுகைகளுக்கு மீட்டெடுக்கவும்.',
        topLearners: 'சிறந்த கற்போர்',
        inviteFriends: 'நண்பர்களை அழை, நாணயங்களைப் பெறு',
        inviteFriendsDesc: 'சரியாக சேரும் ஒவ்வொரு நண்பருக்கும் 500 ஜென் நாணயங்களைப் பெறுங்கள்.',
        copyLink: 'இணைப்பை நகலெடு',
        redeem: 'மீட்டெடு',
        premiumDarkTheme: 'பிரீமியம் இருண்ட தீம்',
        masterBadge: 'மாஸ்டர் பேட்ஜ்',
        oneOnOneMentor: '1-க்கு-1 வழிகாட்டி அழைப்பு',
        adFreeExperience: 'விளம்பரம் இல்லாத அனுபவம்',
        
        // Practice Page
        practiceArena: 'பயிற்சி களம்',
        practiceArenaDesc: 'தினசரி சவால்களுடன் உங்கள் திறமைகளை வளர்த்துக் கொள்ளுங்கள். சரியான ஒவ்வொரு பதிலுக்கும் ஜென் நாணயங்களை சம்பாதியுங்கள்!',
        vocabKataComingSoon: 'வொகாப்காட்டா விரைவில்',
        vocabKataDesc: 'AI-இயங்கும் ஃப்ளாஷ்கார்டுகளுடன் உங்கள் சொல் வங்கியை விரிவாக்கவும்.',
        yourPerformance: 'உங்கள் செயல்திறன்',
        dailyStreak: 'தினசரி வரிசை',
        problemsSolved: 'தீர்க்கப்பட்ட பிரச்சினைகள்',
        proTip: 'தொழில்முறை உதவிக்குறிப்பு',
        proTipDesc: 'நிலைத்தன்மையே முக்கியம்! ஒரு நாளைக்கு 1 காட்டாவைத் தீர்ப்பது நினைவாற்றலை 40% மேம்படுத்துகிறது.',
        days: 'நாட்கள்',
        
        // Courses Page
        exploreOurPrograms: 'எங்கள் திட்டங்களை ஆராயுங்கள்',
        curriculumByExperts: 'உங்கள் தொடர்பு திறன்களை மாற்ற எங்கள் நிபுணர்களால் வடிவமைக்கப்பட்ட பாடத்திட்டம்',
        loadingCoursesMsg: 'பாடங்களை ஏற்றுகிறது...',
        noCoursesAvailable: 'இன்னும் பாடங்கள் கிடைக்கவில்லை',
        noCoursesInCategory: 'இதில் பாடங்கள் இல்லை',
        instructorsCreating: 'எங்கள் பயிற்றுவிப்பாளர்கள் உங்களுக்காக அற்புதமான பாடங்களை உருவாக்குகிறார்கள். விரைவில் மீண்டும் சரிபார்க்கவும்!',
        tryDifferentCategory: 'வேறு வகையைத் தேர்ந்தெடுக்க முயற்சிக்கவும் அல்லது பின்னர் மீண்டும் சரிபார்க்கவும்.',
        viewAllCoursesBtn: 'அனைத்து பாடங்களையும் பார்க்கவும்',
        allLevels: 'அனைத்து நிலைகள்',
        selfPaced: 'சுய வேகம்',
        modules: 'தொகுதிகள்',
        
        // Footer
        about: 'பற்றி',
        aboutUs: 'எங்களை பற்றி',
        ourMission: 'எங்கள் பணி',
        features: 'அம்சங்கள்',
        testimonials: 'சான்றுகள்',
        learning: 'கற்றல்',
        practiceSessions: 'பயிற்சி அமர்வுகள்',
        getFeedback: 'கருத்தைப் பெறுங்கள்',
        trackProgress: 'முன்னேற்றத்தைக் கண்காணிக்கவும்',
        resources: 'ஆதாரங்கள்',
        tutorials: 'பயிற்சிகள்',
        support: 'ஆதரவு',
        connect: 'இணையுங்கள்',
        community: 'சமூகம்',
        events: 'நிகழ்வுகள்',
        volunteer: 'தன்னார்வம்',
        followUs: 'எங்களைப் பின்தொடருங்கள்',
        allRightsReserved: 'அனைத்து உரிமைகளும் பாதுகாக்கப்பட்டவை',
        privacyPolicy: 'தனியுரிமைக் கொள்கை',
        termsOfUse: 'பயன்பாட்டு விதிமுறைகள்',
        
        // Messages
        welcomeBack: 'மீண்டும் வரவேற்கிறோம்',
        loadingMessage: 'ஏற்றுகிறது...',
        noDataAvailable: 'தரவு கிடைக்கவில்லை',
        successMessage: 'வெற்றி!',
        errorMessage: 'அச்சச்சோ! ஏதோ தவறு நடந்தது',
        confirmDelete: 'நிச்சயமாக நீக்க விரும்புகிறீர்களா?',
        saveSuccess: 'வெற்றிகரமாக சேமிக்கப்பட்டது',
        updateSuccess: 'வெற்றிகரமாக புதுப்பிக்கப்பட்டது',
        
        // Footer
        aboutCompany: 'நிறுவனத்தைப் பற்றி',
        privacyPolicy: 'தனியுரிமைக் கொள்கை',
        termsOfService: 'சேவை விதிமுறைகள்',
        contactSupport: 'ஆதரவைத் தொடர்பு கொள்ளுங்கள்',
        followUs: 'எங்களைப் பின்தொடருங்கள்',
        allRightsReserved: 'அனைத்து உரிமைகளும் பாதுகாக்கப்பட்டவை',
    }
};

// Create Language Context
const LanguageContext = createContext();

// Language Provider Component
export const LanguageProvider = ({ children }) => {
    const [language, setLanguage] = useState('en');

    // Load language from localStorage on mount
    useEffect(() => {
        const savedLanguage = localStorage.getItem('language') || 'en';
        setLanguage(savedLanguage);
    }, []);

    // Change language and save to localStorage
    const changeLanguage = (newLanguage) => {
        setLanguage(newLanguage);
        localStorage.setItem('language', newLanguage);
        
        // Dispatch custom event for components that need to know about language change
        window.dispatchEvent(new CustomEvent('languageChanged', { detail: { language: newLanguage } }));
    };

    // Translation helper function
    const t = (key) => {
        return translations[language]?.[key] || translations.en[key] || key;
    };

    const value = {
        language,
        changeLanguage,
        t,
        translations: translations[language] || translations.en
    };

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
};

// Custom hook to use language context
export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};

export default LanguageContext;
